import { apiClient, handleApiRequest } from './client'
import { configApi } from './config'
import type { RadarResponse, RadarConfigResponse, RadarZones, RadarType, UserRadarFilterResponse, UserRadarFilterPayload } from './teacher'
import type { PriorityRequestListItem } from './teacherPriority'
import type { BasicCascadePreviewResponse } from '@/types/teacher-request'

// ==================== Teacher Management ====================

export interface TeacherInfo {
  id: string
  email: string
  first_name: string
  last_name: string
  phone: string | null
  teacher_code: string | null
  is_active: boolean
  background_ids: string[]
  faculty_id: string | null
  notes: string | null
  accept_decline_ratio: number
  total_accepts: number
  total_declines: number
  created_at: string
  updated_at: string
}

export interface TeacherListResponse {
  teachers: TeacherInfo[]
  total: number
  skip: number
  limit: number
  has_more: boolean
}

export interface TeacherCreateRequest {
  email: string
  first_name: string
  last_name: string
  phone?: string
  teacher_code?: string
  background_ids?: string[]
  faculty_id?: string
  notes?: string
}

export interface TeacherUpdateRequest {
  first_name?: string
  last_name?: string
  phone?: string
  teacher_code?: string
  background_ids?: string[]
  faculty_id?: string
  notes?: string
  is_active?: boolean
}

// ==================== Admin Management ====================

export interface AdminInfo {
  id: string
  email: string
  first_name: string
  last_name: string
  is_active: boolean
  created_at: string
}

export interface AdminListResponse {
  admins: AdminInfo[]
  total: number
  has_more: boolean
}

export interface AdminCreateRequest {
  email: string
  first_name: string
  last_name: string
  phone?: string
  notes?: string
}

// ==================== Substitution Management ====================

export interface SubstitutionStatsResponse {
  pending: number
  accepted: number
  declined: number
  cancelled: number
  booked: number
  partially_filled: number
  total: number
}

export interface AdminLessonDetail {
  id: string
  date: string
  time_slot_name: string | null
  subject_name: string | null
  klass_name: string | null
  room_name: string | null
  lesson_number: number | null
  assigned_teacher_id?: string | null
  assigned_teacher_name?: string | null
  assigned_teacher_short_code?: string | null
  status?: string | null
}

export interface AdminResponseDetail {
  teacher_id: string | null
  teacher_name: string
  teacher_short_code: string
  response_type: 'pending' | 'accepted' | 'declined' | 'ignored'
  response_message: string | null
  decline_reason: string | null
  responded_at: string | null
  reminder_count: number
}

export interface AdminPrepDetail {
  status: 'complete' | 'partial' | 'prep_requested' | 'no_prep' | 'none'
  requested_from_teacher_id: string | null
  requested_from_name: string | null
  requested_at: string | null
  reason: string | null
  resolved_at: string | null
}

export interface AdminAssignedTeacher {
  teacher_id: string
  teacher_name: string
  teacher_short_code: string
  lesson_count: number
}

export interface AdminSubstitutionDetail {
  request_id: string
  status: string
  reason: string | null
  notes: string | null
  created_at: string | null
  is_urgent: boolean
  panic_zone_activated: boolean
  needs_admin_assignment?: boolean
  request_mode: string | null
  request_source: string | null
  is_multi_day: boolean
  multi_day_config: Record<string, unknown> | null
  requesting_teacher_id: string
  requesting_teacher_name: string
  requesting_teacher_short_code: string
  requesting_teacher_faculty: string | null
  assigned_teacher_id: string | null
  assigned_teacher_name: string | null
  assigned_teacher_short_code: string | null
  assigned_teachers?: AdminAssignedTeacher[]
  lessons: AdminLessonDetail[]
  total_lesson_count: number
  lesson_request_id: string | null
  responses: AdminResponseDetail[]
  prep: AdminPrepDetail
  hours_until_lesson: number
}

export interface UpdateRadarConfigRequest {
  default_time_range_days?: number
  min_time_range_days?: number
  max_time_range_days?: number
  min_bubble_size?: number
  max_bubble_size?: number
  bubble_size_scale?: string
  teacher_positioning?: string
  show_teacher_labels?: boolean
  show_radius_vectors?: boolean
  max_bubbles_rendered?: number
  show_incoming_in_radar?: boolean
  enable_priority_entry_radar?: boolean
  enable_bubble_aggregation?: boolean
  enable_bubble_splitting?: boolean
  admin_date_fan_enabled?: boolean
  admin_badge_ring_enabled?: boolean
  admin_fan_overlap_fallback?: boolean
  zone_config?: RadarZones
}

export const adminApi = {
  // Teachers
  listTeachers: (params?: {
    skip?: number; limit?: number; search?: string
    faculty_id?: string; background_id?: string
    min_ratio?: number; max_ratio?: number; min_accepts?: number
  }) => {
    const qs = new URLSearchParams()
    if (params?.skip !== undefined) qs.set('skip', String(params.skip))
    if (params?.limit !== undefined) qs.set('limit', String(params.limit))
    if (params?.search) qs.set('search', params.search)
    if (params?.faculty_id) qs.set('faculty_id', params.faculty_id)
    if (params?.background_id) qs.set('background_id', params.background_id)
    if (params?.min_ratio !== undefined) qs.set('min_ratio', String(params.min_ratio))
    if (params?.max_ratio !== undefined) qs.set('max_ratio', String(params.max_ratio))
    if (params?.min_accepts !== undefined) qs.set('min_accepts', String(params.min_accepts))
    const query = qs.toString() ? `?${qs.toString()}` : ''
    return handleApiRequest<TeacherListResponse>(apiClient.get(`/api/v1/admin/teachers${query}`))
  },

  searchUsers: (params?: { search?: string; limit?: number; role?: string }) => {
    const qs = new URLSearchParams()
    if (params?.search) qs.set('search', params.search)
    if (params?.limit !== undefined) qs.set('limit', String(params.limit))
    if (params?.role) qs.set('role', params.role)
    const query = qs.toString() ? `?${qs.toString()}` : ''
    return handleApiRequest<{ users: Array<{ user_id: string; email: string; first_name?: string | null; last_name?: string | null; role: string }> }>(
      apiClient.get(`/api/v1/admin/users/search${query}`),
    )
  },

  getNoSubstituteRecipients: () =>
    handleApiRequest<{ can_edit: boolean; configured_ids: string[]; recipients: { user_id: string; name: string }[] }>(
      apiClient.get('/api/v1/admin/substitution-request/no-substitute-recipients'),
    ),

  updateNoSubstituteRecipients: (recipient_user_ids: string[]) =>
    handleApiRequest<{ message: string; success: boolean }>(
      apiClient.put('/api/v1/admin/substitution-request/no-substitute-recipients', { recipient_user_ids }),
    ),

  createTeacher: (data: TeacherCreateRequest) =>
    handleApiRequest<TeacherInfo>(apiClient.post('/api/v1/admin/teachers', data)),

  getTeacher: (id: string) =>
    handleApiRequest<TeacherInfo>(apiClient.get(`/api/v1/admin/teachers/${id}`)),

  updateTeacher: (id: string, data: TeacherUpdateRequest) =>
    handleApiRequest<TeacherInfo>(apiClient.patch(`/api/v1/admin/teachers/${id}`, data)),

  deleteTeacher: (id: string) =>
    handleApiRequest(apiClient.delete(`/api/v1/admin/teachers/${id}`)),

  assignTeacherCode: (teacherId: string, teacherCode: string) =>
    handleApiRequest(apiClient.post(`/api/v1/admin/teachers/${teacherId}/assign-code`, { teacher_code: teacherCode })),

  // Admins
  listAdmins: (params?: { skip?: number; limit?: number }) => {
    const qs = new URLSearchParams()
    if (params?.skip !== undefined) qs.set('skip', String(params.skip))
    if (params?.limit !== undefined) qs.set('limit', String(params.limit))
    const query = qs.toString() ? `?${qs.toString()}` : ''
    return handleApiRequest<AdminListResponse>(apiClient.get(`/api/v1/admin/admins${query}`))
  },

  createAdmin: (data: AdminCreateRequest) =>
    handleApiRequest(apiClient.post('/api/v1/admin/admins', data)),

  removeAdmin: (id: string) =>
    handleApiRequest(apiClient.delete(`/api/v1/admin/admins/${id}`)),

  // Substitutions
  listSubstitutions: (params?: { status_filter?: string; requester_id?: string; skip?: number; limit?: number }) => {
    const qs = new URLSearchParams()
    if (params?.status_filter) qs.set('status_filter', params.status_filter)
    if (params?.requester_id) qs.set('requester_id', params.requester_id)
    if (params?.skip !== undefined) qs.set('skip', String(params.skip))
    if (params?.limit !== undefined) qs.set('limit', String(params.limit))
    const query = qs.toString() ? `?${qs.toString()}` : ''
    return handleApiRequest(apiClient.get(`/api/v1/admin/substitutions${query}`))
  },

  deleteSubstitution: (id: string) =>
    handleApiRequest(apiClient.delete(`/api/v1/admin/substitutions/${id}`)),

  getSubstitutionStats: () =>
    handleApiRequest<SubstitutionStatsResponse>(apiClient.get('/api/v1/admin/substitutions/statistics')),

  // Audit logs
  listAuditLogs: (params?: { skip?: number; limit?: number; action_filter?: string }) => {
    const qs = new URLSearchParams()
    if (params?.skip !== undefined) qs.set('skip', String(params.skip))
    if (params?.limit !== undefined) qs.set('limit', String(params.limit))
    if (params?.action_filter) qs.set('action_filter', params.action_filter)
    const query = qs.toString() ? `?${qs.toString()}` : ''
    return handleApiRequest(apiClient.get(`/api/v1/admin/audit-logs${query}`))
  },

  // Admin Radar
  getAdminRadar: (timeRangeDays?: number, signal?: AbortSignal) => {
    const q = timeRangeDays ? `?time_range_days=${timeRangeDays}` : ''
    return handleApiRequest<RadarResponse>(apiClient.get(`/api/v1/radar/bubbles${q}`, { signal }))
  },

  getRadarConfig: async () => {
    return handleApiRequest<RadarConfigResponse>(apiClient.get('/api/v1/radar/config'))
  },

  // Per-user radar filter preferences (filter-fix-v2)
  getRadarUserFilters: (radarType: RadarType, signal?: AbortSignal) =>
    handleApiRequest<UserRadarFilterResponse>(
      apiClient.get(`/api/v1/radar/user-filters`, { params: { radar_type: radarType }, signal })
    ),
  saveRadarUserFilters: (payload: UserRadarFilterPayload) =>
    handleApiRequest<UserRadarFilterResponse>(
      apiClient.put(`/api/v1/radar/user-filters`, payload)
    ),

  updateRadarConfig: async (data: UpdateRadarConfigRequest) => {
    const result = await configApi.updateConfig('radar', data as unknown as Record<string, unknown>)
    return result as unknown as RadarConfigResponse
  },

  getSubstitutionDetail: (requestId: string) =>
    handleApiRequest<AdminSubstitutionDetail>(
      apiClient.get(`/api/v1/admin/substitutions/${requestId}/detail`)
    ),

  getSubstitutionCard: (requestId: string) =>
    handleApiRequest<PriorityRequestListItem>(
      apiClient.get(`/api/v1/admin/substitutions/${requestId}/card`)
    ),

  getCascadePreview: (requestId: string) =>
    handleApiRequest<BasicCascadePreviewResponse>(
      apiClient.get(`/api/v1/admin/substitutions/${requestId}/cascade-preview`)
    ),

  cancelSubstitution: (requestId: string) =>
    handleApiRequest(apiClient.patch(`/api/v1/admin/substitutions/${requestId}/cancel`)),

  cancelSubstitutionLessons: (lessonRequestIds: string[], reason?: string) =>
    handleApiRequest(
        apiClient.post(`/api/v1/admin/substitutions/cancel-lessons`, {
            lesson_request_ids: lessonRequestIds,
            reason,
        })
    ),

  assignSubstitution: (requestId: string, substituteTeacherId: string, lessonIds?: string[]) =>
    handleApiRequest(apiClient.post(`/api/v1/admin/substitutions/${requestId}/assign`, {
      substitute_teacher_id: substituteTeacherId,
      lesson_ids: lessonIds,
    })),
}
